// Review rules from brief §3 ("Review page") and prototype/src/store.jsx (ipBlock, ratingFrom).

export const REVIEW_TEXT_MIN = 10;
export const REVIEW_TEXT_MAX = 1000;
export const REVIEWER_NAME_MAX = 80;

const DAY_MS = 24 * 60 * 60 * 1000;

export type IpRuleScope = 'all' | 'per-astrologer';

export interface IpRuleSettings {
  enabled: boolean;
  windowDays: number;
  scope: IpRuleScope;
}

export interface PriorReviewFromIp {
  astrologerId: string;
  createdAt: Date;
}

// The most recent review from the same IP that blocks a new one, or null. A review blocks while it
// is strictly newer than `now - windowDays`: one submitted exactly windowDays ago no longer blocks.
export function ipRuleBlockingReview(
  settings: IpRuleSettings,
  priorFromSameIp: readonly PriorReviewFromIp[],
  astrologerId: string,
  now: Date = new Date(),
): PriorReviewFromIp | null {
  if (!settings.enabled) return null;
  const since = now.getTime() - settings.windowDays * DAY_MS;
  let latest: PriorReviewFromIp | null = null;
  for (const r of priorFromSameIp) {
    if (settings.scope === 'per-astrologer' && r.astrologerId !== astrologerId) continue;
    if (r.createdAt.getTime() <= since) continue;
    if (!latest || r.createdAt > latest.createdAt) latest = r;
  }
  return latest;
}

// When the IP may review again after a blocking review.
export function ipRuleAllowedFrom(blockingReviewAt: Date, windowDays: number): Date {
  return new Date(blockingReviewAt.getTime() + windowDays * DAY_MS);
}

export interface RatingSummary {
  rating: number; // rounded to one decimal; 0 when there are no reviews
  count: number;
  distribution: { stars: number; count: number }[]; // 5★ down to 1★
}

export function roundRating(avg: number): number {
  return Math.round(avg * 10) / 10;
}

export function summarizeRatings(ratings: readonly number[]): RatingSummary {
  const distribution = [5, 4, 3, 2, 1].map((stars) => ({
    stars,
    count: ratings.filter((r) => r === stars).length,
  }));
  if (ratings.length === 0) return { rating: 0, count: 0, distribution };
  const avg = ratings.reduce((s, r) => s + r, 0) / ratings.length;
  return { rating: roundRating(avg), count: ratings.length, distribution };
}

// Plain text only (brief §3): strip control characters and collapse runs of blank lines. HTML is
// never interpreted anywhere it's displayed (React escapes it), so tags are kept as typed text.
export function sanitizeReviewText(text: string): string {
  return (
    text
      // eslint-disable-next-line no-control-regex -- intentionally removing control characters
      .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '')
      .replace(/\r\n?/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim()
  );
}
